<script setup lang="ts">
import { type Component, computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import type { Placement } from '@/modules/common/types/placement.ts';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const {
  text,
  is = 'span',
  placement = 'top',
  tooltipTitle,
  align,
} = defineProps<{
  text: string;
  is?: string | Component;
  placement?: Placement;
  tooltipTitle?: string;
  align?: 'center' | 'left';
}>();

const contentRef = useTemplateRef('content');
const disabled = ref(true);

const tooltipAlign = computed(() => align ?? (tooltipTitle ? 'left' : 'center'));

function updateDisabled() {
  const el = contentRef.value;
  disabled.value = !el || el.scrollWidth <= el.clientWidth;
}

onMounted(() => {
  void nextTick(updateDisabled);
});

watch(
  () => text,
  () => {
    void nextTick(updateDisabled);
  }
);
</script>

<template>
  <UiTooltip
    :is="is"
    class="ui-ellipsis-text"
    :text="tooltipTitle ? undefined : text"
    :placement="placement"
    :align="tooltipAlign"
    :disabled="disabled"
    @mouseenter="updateDisabled"
  >
    <template
      v-if="tooltipTitle"
      #tooltip
    >
      <div class="tooltip">
        <p class="tooltip-title">{{ $maybeT(tooltipTitle) }}</p>
        <p class="tooltip-text">{{ text }}</p>
      </div>
    </template>

    <span
      ref="content"
      class="content"
    >{{ text }}</span>
  </UiTooltip>
</template>

<style scoped lang="scss">
.ui-ellipsis-text {
  display: block;
  min-width: 0;
  max-width: 100%;
}

.content {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tooltip {
  display: flex;
  max-width: 280px;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.tooltip-title {
  margin: 0;
  color: var(--color-gray-1000);
  font-size: var(--font-size);
  line-height: var(--line-height);
  font-weight: var(--font-weight-bold);
}

.tooltip-text {
  margin: 0;
  white-space: pre-line;
  word-break: break-word;
  color: var(--color-gray-1000);
  font-size: var(--font-size);
  line-height: var(--line-height);
}
</style>
